import axios from 'axios'

import cookieUtility from '../utils/cookie.utility.js'
import httpUtility from '../utils/http.utility.js'

import apiClient from './client.js'

const AUTH_URL = import.meta.env.VITE_AUTH_URL || ''

async function searchUsersAccounts({ search = '*', page = 1, itemsPerPage = 100, signal } = {}) {
  const response = await apiClient.get(`/account/search-users-accounts?${httpUtility.query({ search, page, itemsPerPage })}`, {
    signal,
  })
  return {
    accounts: response.data.accounts,
    total: response.data.totalAccessibleAccountLength,
  }
}

// On `403 { code: "CSRF_REISSUED" }` the DAL has set a fresh csrf_token cookie
// and asks us to retry once — we do, using the new cookie value.
async function switchAccount(accountId) {
  let csrfToken = cookieUtility.get(`${import.meta.env.VITE_COOKIE_PREFIX}csrf_token`)

  try {
    return await axios.post(
      `${AUTH_URL}/account/switch`,
      { accountId },
      {
        withCredentials: true,
        headers: csrfToken ? { 'X-CSRF-Token': csrfToken } : {},
      },
    )
  } catch (error) {
    if (error.response?.status !== 403 || error.response?.data?.code !== 'CSRF_REISSUED') throw error
    // Retry with the fresh csrf_token cookie the DAL just set.
    csrfToken = cookieUtility.get(`${import.meta.env.VITE_COOKIE_PREFIX}csrf_token`)
    return axios.post(
      `${AUTH_URL}/account/switch`,
      { accountId },
      {
        withCredentials: true,
        headers: csrfToken ? { 'X-CSRF-Token': csrfToken } : {},
      },
    )
  }
}

async function updateEmailNotificationSettings(payload) {
  const response = await apiClient.patch('/account/email-notification-settings/', payload)
  return response.data.settings
}

async function getAdminAccountList(filters = {}, { page, pageSize, signal } = {}) {
  const response = await apiClient.get(
    `/account/admin/list?${httpUtility.query({
      search: filters.search,
      active: filters.active,
      group: filters.group,
      page,
      itemsPerPage: pageSize,
    })}`,
    { signal },
  )
  return {
    items: response.data.accounts ?? [],
    total: response.data.totalLength ?? 0,
    page: response.data.page ?? page,
    pageSize: response.data.itemsPerPage ?? pageSize,
  }
}

class AccountService {
  get = {
    /**
     * Load accounts visible to the signed-in user.
     *
     * @param {object} [options]
     * @param {string} [options.search='*']
     * @param {number} [options.page=1]
     * @param {number} [options.itemsPerPage=100]
     * @param {AbortSignal} [options.signal]
     * @returns {Promise<{ accounts: object[], total: number }>} Unwrapped from `response.data`.
     */
    async search(options) {
      return searchUsersAccounts(options)
    },

    admin: {
      /**
       * SuperAdmin paginated account dump (`GET /account/admin/list`).
       *
       * @param {object} [filters]
       * @param {string} [filters.search]
       * @param {string|boolean} [filters.active]
       * @param {string} [filters.group]
       * @param {object} [params]
       * @param {number} [params.page]
       * @param {number} [params.pageSize]
       * @param {AbortSignal} [params.signal]
       */
      async list(filters = {}, params = {}) {
        return getAdminAccountList(filters, params)
      },
    },
  }

  update = {
    /**
     * Switch the DAL session to a different account.
     *
     * @param {string} accountId
     * @returns {Promise<import('axios').AxiosResponse>}
     */
    async switch(accountId) {
      return switchAccount(accountId)
    },

    /**
     * Partial update of operational email flags + recipients.
     *
     * @param {{ _id: string, settings: object }} payload
     * @returns {Promise<object>}
     */
    async emailNotificationSettings(payload) {
      return updateEmailNotificationSettings(payload)
    },
  }
}

export default new AccountService()
